import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography, Pagination, Stack } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import ExpenseCategoryCard from "../components/expenseCategories/ExpenseCategoryCard";
import CreateExpenseCategoryModal from "../components/expenseCategories/CreateExpenseCategoryModal";
import UpdateExpenseCategoryModal from "../components/expenseCategories/UpdateExpenseCategoryModal";
import AppCircularProgress from "../components/common/AppCircularProgress";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getExpenseCategories,
  deleteExpenseCategory,
} from "../redux/actions/expenseCategoryActions";
import PageError from "../components/common/PageError";
import { expenseCategoryActions } from "../redux/slices/expenseCategory";
import { ExpenseCategoryResponseDto } from "../shared/dtos/expenseCategory/ExpenseCategoryResponseDto";

const ExpenseCategoriesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: expenseCategories,
    pagination,
    loadingFetchAll: loadingexpenseCategories,
    errorFetchAll: errorLoadingexpenseCategories,
    serverStatus,
    loadingDelete: loadingExpenseCategoryDelete,
    loadingUpdate: loadingExpenseCategoryUpdate,
    loadingCreate: loadingExpenseCategoryCreate,
    errorCreate: errorCreatingExpenseCategory,
    errorUpdate: errorUpdatingExpenseCategory,
  } = useAppSelector((state) => state.expenseCategory);

  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(12);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] =
    useState<ExpenseCategoryResponseDto | null>(null);

  const canCreate = hasPermission("expense_category:create");
  const canUpdate = hasPermission("expense_category:update");
  const canDelete = hasPermission("expense_category:delete");

  const fetchCategories = useCallback(() => {
    dispatch(
      getExpenseCategories({ page, size: limit, search: debouncedSearch }),
    );
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback(
    () => !loadingExpenseCategoryCreate && setIsCreateModalOpen(false),
    [loadingExpenseCategoryCreate],
  );

  const handleOpenUpdate = (cat: ExpenseCategoryResponseDto): void => {
    setSelectedCategory(cat);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback(() => {
    if (!loadingExpenseCategoryUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedCategory(null);
    }
  }, [loadingExpenseCategoryUpdate]);

  const handleOpenDelete = (cat: ExpenseCategoryResponseDto): void => {
    setSelectedCategory(cat);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback(() => {
    if (!loadingExpenseCategoryDelete) {
      setIsDeleteModalOpen(false);
      setSelectedCategory(null);
    }
  }, [loadingExpenseCategoryDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedCategory) dispatch(deleteExpenseCategory(selectedCategory.id));
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();
    dispatch(expenseCategoryActions.setServerStatus(null));
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const renderContent = (): React.JSX.Element => {
    if (loadingexpenseCategories) return <AppCircularProgress />;
    if (errorLoadingexpenseCategories)
      return (
        <PageError
          error={errorLoadingexpenseCategories}
          onRetry={fetchCategories}
        />
      );

    if (!expenseCategories || expenseCategories.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No expense categories found.</Typography>
        </Box>
      );

    return (
      <>
        <Grid container spacing={3}>
          {expenseCategories.map((cat) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={cat.id}>
              <ExpenseCategoryCard
                category={cat}
                onEdit={canUpdate ? () => handleOpenUpdate(cat) : undefined}
                onDelete={canDelete ? () => handleOpenDelete(cat) : undefined}
              />
            </Grid>
          ))}
        </Grid>
        {pagination && pagination.totalPages! > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_e, v) => setPage(v)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Expense Categories"
      subtitle="Group your business expenses and link them to your general ledger."
      breadcrumbs={[
        { label: "Dashboard", to: "/dashboard" },
        { label: "Expense Categories", to: undefined },
      ]}
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Category
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search categories..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreateExpenseCategoryModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingExpenseCategoryCreate}
          error={errorCreatingExpenseCategory}
        />
      )}
      {isUpdateModalOpen && selectedCategory && (
        <UpdateExpenseCategoryModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          category={selectedCategory}
          loading={loadingExpenseCategoryUpdate}
          error={errorUpdatingExpenseCategory}
        />
      )}
      {isDeleteModalOpen && selectedCategory && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Category"
          description={`Are you sure you want to delete "${selectedCategory.name}"?`}
          loading={loadingExpenseCategoryDelete}
          severity="error"
        />
      )}
    </PageContainer>
  );
};

export default ExpenseCategoriesPage;
